import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { BACKEND_URL, formatErr, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Flame, Sparkles, ShieldCheck, Truck, Gift, Clock, Star,
  CheckCircle2, ChevronDown, MapPin, IndianRupee, Trophy, Zap, Users, X
} from "lucide-react";

// ------------------------------------------------------------------
// /spin — Stylefire Mega Spin & Win Festival
// High-converting mobile-first landing with countdown, wheel, FAQ,
// testimonials, and live Razorpay payment that unlocks the spin.
// ------------------------------------------------------------------

const WHEEL_SIZE = 320;            // px on mobile baseline
const SPIN_DURATION_MS = 5200;

export default function Spin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [phase, setPhase] = useState("idle");       // idle | paying | spinning | won
  const [spinId, setSpinId] = useState(null);
  const [prize, setPrize] = useState(null);
  const [wheelDeg, setWheelDeg] = useState(0);
  const [showClaim, setShowClaim] = useState(false);

  useEffect(() => { api.get("/spin/config").then(r => setCfg(r.data)); }, []);

  const prizes = cfg?.prizes || [];
  const slotCount = prizes.length || 8;
  const sliceAngle = 360 / slotCount;

  const startPayment = async () => {
    if (!user) { toast.error("Please login to spin"); nav("/login"); return; }
    if (phase !== "idle") return;
    try {
      setPhase("paying");
      const { data } = await api.post("/spin/start");
      await openRazorpay(data);
    } catch (e) {
      setPhase("idle");
      toast.error(formatErr(e?.response?.data?.detail) || "Could not start payment");
    }
  };

  const openRazorpay = (data) => new Promise((resolve) => {
    const launch = () => {
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "Stylefire Spin & Win",
        description: "Mega Spin Festival — ₹79 guaranteed reward",
        theme: { color: "#FF4500" },
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.mobile || "" },
        handler: async (res) => {
          try {
            const { data: vr } = await api.post("/spin/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
              spin_id: data.spin_id,
            });
            setSpinId(vr.spin_id);
            runSpinAnimation(vr.winning_index, vr.prize);
          } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail) || "Payment verification failed");
            setPhase("idle");
          }
          resolve();
        },
        modal: {
          ondismiss: () => { setPhase("idle"); toast.info("Payment cancelled"); resolve(); },
        },
      };
      if (window.Razorpay) new window.Razorpay(options).open();
      else toast.error("Payment SDK failed to load");
    };

    if (window.Razorpay) launch();
    else {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = launch;
      s.onerror = () => { toast.error("Payment SDK failed to load"); setPhase("idle"); resolve(); };
      document.body.appendChild(s);
    }
  });

  const runSpinAnimation = (idx, prizeObj) => {
    setPhase("spinning");
    // Multiple full rotations + land mid-slice for the winning index.
    // The wheel rotates clockwise. The pointer sits at top (0deg).
    // We need slice idx center under pointer => target angle = -(idx * sliceAngle + sliceAngle/2)
    const fullTurns = 6;
    const landing = -(idx * sliceAngle + sliceAngle / 2);
    const finalDeg = fullTurns * 360 + landing;
    setWheelDeg(finalDeg);
    setTimeout(() => {
      setPrize(prizeObj);
      setPhase("won");
      // confetti pulse
      try { window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); } catch (_e) { /* noop */ }
    }, SPIN_DURATION_MS);
  };

  return (
    <main className="bg-gradient-to-b from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white min-h-screen">
      {/* ───────── HERO ───────── */}
      <section className="relative px-4 sm:px-6 lg:px-8 pt-10 pb-6 max-w-5xl mx-auto text-center">
        <Countdown />

        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FF4500]/15 border border-[#FF4500]/40 text-[#FFB088] text-xs font-bold tracking-widest mt-4" data-testid="spin-badge">
          <Flame size={14} className="text-[#FF4500]"/> MEGA SPIN FESTIVAL · 2026
        </div>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold mt-4 leading-[1.05]">
          <span className="block">Pay just</span>
          <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#FF4500] via-[#FF8A3D] to-[#FFC85C]">₹79 & Win Big</span>
        </h1>
        <p className="text-slate-300 mt-4 text-base sm:text-lg max-w-xl mx-auto">
          Every spin is a <b className="text-white">guaranteed</b> reward. Fashion, footwear, electronics & more — delivered PAN India.
        </p>

        {/* Social proof bar */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-6 text-sm text-slate-300">
          <span className="inline-flex items-center gap-1.5"><Users size={14} className="text-[#FFB088]"/> 12,478 spins today</span>
          <span className="inline-flex items-center gap-1.5"><Star size={14} className="text-[#FFC85C] fill-[#FFC85C]"/> 4.8 rating</span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#10B981]"/> Razorpay secure</span>
        </div>
      </section>

      {/* ───────── WHEEL + CTA ───────── */}
      <section className="px-4 sm:px-6 max-w-2xl mx-auto pb-10">
        <div className="relative mx-auto" style={{ width: "min(90vw, 460px)" }}>
          {/* glow */}
          <div className="absolute inset-0 -m-6 rounded-full bg-[#FF4500]/30 blur-3xl opacity-50 animate-pulse"/>
          <div className="relative">
            <Wheel
              prizes={prizes}
              slotCount={slotCount}
              sliceAngle={sliceAngle}
              wheelDeg={wheelDeg}
              spinning={phase === "spinning"}
            />
            <Pointer />
          </div>
        </div>

        <div className="mt-8 text-center">
          {phase === "idle" && (
            <button
              data-testid="spin-pay-btn"
              onClick={startPayment}
              className="group inline-flex items-center gap-2 px-8 sm:px-12 py-4 sm:py-5 rounded-full font-bold text-lg sm:text-xl btn-fire shadow-[0_10px_40px_-12px_rgba(255,69,0,0.7)] hover:scale-[1.03] transition-transform"
            >
              <Zap size={20} className="group-hover:rotate-12 transition" /> SPIN NOW FOR ₹79
            </button>
          )}
          {phase === "paying" && (
            <div className="text-slate-300 inline-flex items-center gap-2"><Clock className="animate-pulse"/> Opening secure payment…</div>
          )}
          {phase === "spinning" && (
            <div className="text-[#FFC85C] inline-flex items-center gap-2 font-semibold"><Sparkles className="animate-spin"/> Spinning… your luck is on fire 🔥</div>
          )}
          {phase === "won" && prize && (
            <WinBanner prize={prize} onClaim={() => setShowClaim(true)} />
          )}

          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1"><ShieldCheck size={12}/> SSL secured</span>
            <span className="inline-flex items-center gap-1"><Truck size={12}/> Free PAN-India delivery</span>
            <span className="inline-flex items-center gap-1"><Gift size={12}/> Every spin wins</span>
          </div>
        </div>
      </section>

      {/* ───────── WHY JOIN ───────── */}
      <section className="bg-white text-slate-900 rounded-t-[40px] mt-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <SectionHeader eyebrow="WHY JOIN" title="100% win-rate. Real products." sub="No catches. No coupons. A real reward shipped to your doorstep."/>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-8">
            {[
              { i: CheckCircle2, t: "Every Spin Wins", s: "Guaranteed reward" },
              { i: Sparkles,     t: "Premium Products", s: "Curated catalog" },
              { i: Clock,        t: "Limited Time", s: "Today only" },
              { i: Truck,        t: "PAN India", s: "Free delivery" },
              { i: ShieldCheck,  t: "Trusted",  s: "Razorpay secured" },
            ].map((c, i) => (
              <div key={c.t} className="border border-slate-100 rounded-2xl p-4 text-center hover:shadow-md transition" data-testid={`why-card-${i}`}>
                <span className="grid place-items-center w-11 h-11 mx-auto rounded-xl bg-[#FF4500]/10 text-[#FF4500] mb-3"><c.i size={20} strokeWidth={1.7}/></span>
                <div className="font-semibold text-sm">{c.t}</div>
                <div className="text-xs text-slate-500 mt-0.5">{c.s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── HOW IT WORKS ───────── */}
      <section className="bg-slate-50 text-slate-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <SectionHeader eyebrow="HOW IT WORKS" title="From spin to doorstep in 5 steps." sub="Average claim time: under 60 seconds."/>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-8">
            {[
              { n: 1, t: "Pay ₹79", s: "Secure Razorpay checkout" },
              { n: 2, t: "Spin the Wheel", s: "Watch the magic happen" },
              { n: 3, t: "See Your Reward", s: "Instant prize reveal" },
              { n: 4, t: "Fill Delivery Details", s: "Address & contact" },
              { n: 5, t: "Redeem Your Product", s: "Ships within 48hrs" },
            ].map((s) => (
              <div key={s.n} className="bg-white rounded-2xl p-5 border border-slate-100 relative" data-testid={`how-step-${s.n}`}>
                <span className="absolute -top-3 left-5 w-7 h-7 rounded-full bg-[#FF4500] text-white text-sm font-bold grid place-items-center">{s.n}</span>
                <div className="font-semibold text-sm mt-2">{s.t}</div>
                <div className="text-xs text-slate-500 mt-1">{s.s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── PRIZE GRID ───────── */}
      <section className="bg-white text-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <SectionHeader eyebrow="POSSIBLE REWARDS" title="Real prizes. Real delivery." sub={`${prizes.length} curated prizes. You win one — guaranteed.`}/>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mt-8">
            {prizes.map((p) => (
              <div key={p.prize_id} className="relative rounded-2xl overflow-hidden border border-slate-100 group" data-testid={`prize-${p.prize_id}`}>
                <div className="aspect-square bg-slate-100 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
                </div>
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{p.category}</div>
                  <div className="text-sm font-semibold mt-1 line-clamp-1">{p.title}</div>
                  <div className="text-xs text-slate-500 mt-1">Worth up to {inr(p.estimated_value)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── TESTIMONIALS ───────── */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <SectionHeader eyebrow="REAL WINNERS" title="Trusted by thousands." sub="What our spinners say." dark/>
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            {TESTIMONIALS.map((t, i) => (
              <div key={t.name} className="bg-white/5 rounded-2xl p-5 border border-white/10" data-testid={`testimonial-${i}`}>
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({length:5}).map((_,n) => <Star key={n} size={14} className="text-[#FFC85C] fill-[#FFC85C]"/>)}
                </div>
                <p className="text-sm text-slate-200 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3 mt-4">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF4500] to-[#FFC85C] grid place-items-center text-xs font-bold">{t.name[0]}</div>
                  <div>
                    <div className="text-sm font-semibold">{t.name}</div>
                    <div className="text-xs text-slate-400">Won {t.prize} · {t.city}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className="bg-white text-slate-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <SectionHeader eyebrow="FAQ" title="Frequently asked." sub="Everything you need to know."/>
          <div className="mt-6 space-y-2">
            {FAQS.map((f, i) => <FAQItem key={f.q} q={f.q} a={f.a} testid={`faq-${i}`} />)}
          </div>
        </div>
      </section>

      {/* ───────── STICKY CTA (mobile) ───────── */}
      {phase === "idle" && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/95 to-transparent">
          <button
            data-testid="spin-pay-btn-sticky"
            onClick={startPayment}
            className="w-full btn-fire py-4 rounded-full font-bold text-lg shadow-2xl inline-flex items-center justify-center gap-2"
          >
            <Zap size={18}/> SPIN NOW FOR ₹79
          </button>
        </div>
      )}

      {/* ───────── FOOTER NOTE ───────── */}
      <section className="bg-[#0F172A] text-slate-400 text-center py-8 px-4">
        <div className="font-display font-bold text-white text-lg flex items-center justify-center gap-2"><Flame className="text-[#FF4500]"/> Stylefire</div>
        <p className="text-xs mt-2">Where Style Meets Fire 🔥 · One spin per payment · Prize allocation is server-verified and fair</p>
        <Link to="/" className="text-xs text-[#FFB088] underline mt-3 inline-block">← Back to Stylefire</Link>
      </section>

      {/* ───────── CLAIM MODAL ───────── */}
      {showClaim && prize && spinId && (
        <ClaimModal spinId={spinId} prize={prize} onClose={() => setShowClaim(false)} onClaimed={(orderId) => { setShowClaim(false); nav(`/account/orders/${orderId}`); }} />
      )}
    </main>
  );
}

// ============== Components ==============
function Wheel({ prizes, slotCount, sliceAngle, wheelDeg, spinning }) {
  const radius = 50;
  const cx = 50, cy = 50;

  const polar = (deg, r = radius) => {
    const rad = (deg - 90) * Math.PI / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };

  const slices = useMemo(() => {
    return Array.from({ length: slotCount }).map((_, i) => {
      const startAngle = i * sliceAngle;
      const endAngle = (i + 1) * sliceAngle;
      const [x1, y1] = polar(startAngle);
      const [x2, y2] = polar(endAngle);
      const large = sliceAngle > 180 ? 1 : 0;
      const d = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} Z`;
      // text position at mid radius
      const [tx, ty] = polar(startAngle + sliceAngle / 2, radius * 0.62);
      const textAngle = startAngle + sliceAngle / 2;
      const p = prizes[i];
      return { d, tx, ty, textAngle, color: p?.color || "#004BCA", title: p?.title || "Prize" };
    });
  }, [slotCount, sliceAngle, prizes]);

  return (
    <div className="relative" style={{ width: "100%", aspectRatio: "1/1" }}>
      <div
        className="w-full h-full"
        style={{
          transform: `rotate(${wheelDeg}deg)`,
          transition: spinning ? `transform ${SPIN_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)` : "transform 0.4s ease-out",
        }}
        data-testid="spin-wheel"
      >
        <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
          <defs>
            <radialGradient id="ring" cx="50%" cy="50%" r="50%">
              <stop offset="92%" stopColor="#FFC85C" stopOpacity="0"/>
              <stop offset="98%" stopColor="#FFC85C" stopOpacity="1"/>
              <stop offset="100%" stopColor="#FF4500" stopOpacity="1"/>
            </radialGradient>
          </defs>
          {slices.map((s, i) => (
            <g key={i}>
              <path d={s.d} fill={s.color} stroke="#fff" strokeWidth="0.5"/>
              <text
                x={s.tx} y={s.ty}
                transform={`rotate(${s.textAngle} ${s.tx} ${s.ty})`}
                fill="#fff" fontSize="3.2" fontWeight="700"
                textAnchor="middle" dominantBaseline="middle"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {truncate(s.title, 16)}
              </text>
            </g>
          ))}
          <circle cx={cx} cy={cy} r={radius} fill="none" stroke="url(#ring)" strokeWidth="2"/>
          <circle cx={cx} cy={cy} r="8" fill="#fff"/>
          <circle cx={cx} cy={cy} r="5" fill="#FF4500"/>
        </svg>
      </div>
    </div>
  );
}

function Pointer() {
  return (
    <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10">
      <div className="w-0 h-0 border-l-[18px] border-r-[18px] border-t-[28px] border-l-transparent border-r-transparent border-t-[#FFC85C] drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]"/>
      <div className="w-4 h-4 rounded-full bg-[#FFC85C] mx-auto -mt-1 ring-4 ring-[#FF4500]"/>
    </div>
  );
}

function Countdown() {
  // Counts down to next midnight (resets daily)
  const [tl, setTl] = useState(() => msToMidnight());
  useEffect(() => {
    const t = setInterval(() => setTl(msToMidnight()), 1000);
    return () => clearInterval(t);
  }, []);
  const { h, m, s } = formatMs(tl);
  return (
    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur" data-testid="countdown">
      <Clock size={14} className="text-[#FFC85C]"/>
      <span className="text-xs text-slate-200">Festival ends in</span>
      <span className="font-mono text-sm font-bold text-white tabular-nums">{h}h : {m}m : {s}s</span>
    </div>
  );
}

function msToMidnight() {
  const now = new Date();
  const m = new Date(now); m.setHours(24, 0, 0, 0);
  return Math.max(0, m - now);
}
function formatMs(ms) {
  const s = Math.floor(ms / 1000) % 60;
  const m = Math.floor(ms / 60000) % 60;
  const h = Math.floor(ms / 3600000);
  return { h: String(h).padStart(2,"0"), m: String(m).padStart(2,"0"), s: String(s).padStart(2,"0") };
}

function SectionHeader({ eyebrow, title, sub, dark }) {
  return (
    <div className="text-center">
      <div className={`text-xs font-bold tracking-[0.3em] ${dark ? "text-[#FFB088]" : "text-[#FF4500]"}`}>{eyebrow}</div>
      <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2 tracking-tight">{title}</h2>
      {sub && <p className={`mt-2 text-sm ${dark ? "text-slate-300" : "text-slate-500"}`}>{sub}</p>}
    </div>
  );
}

function WinBanner({ prize, onClaim }) {
  return (
    <div className="relative mt-2 p-5 sm:p-6 rounded-3xl bg-gradient-to-br from-[#FFC85C] via-[#FF8A3D] to-[#FF4500] text-white text-left shadow-2xl overflow-hidden fade-up" data-testid="win-banner">
      <Sparkles className="absolute top-3 right-3 opacity-50" size={24}/>
      <div className="flex items-center gap-4">
        {prize.image && <img src={prize.image} alt={prize.title} className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white/40"/>}
        <div className="flex-1">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/25 text-[10px] font-bold tracking-widest"><Trophy size={11}/> YOU WON</div>
          <div className="font-display text-2xl font-extrabold mt-1.5 leading-tight">{prize.title}</div>
          <div className="text-xs opacity-90 mt-1">{prize.category} · Worth up to {inr(prize.estimated_value)}</div>
        </div>
      </div>
      <button data-testid="claim-prize-btn" onClick={onClaim} className="w-full mt-5 py-3.5 rounded-full bg-white text-[#FF4500] font-bold inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform">
        Claim Now & Get Delivered <CheckCircle2 size={18}/>
      </button>
    </div>
  );
}

function ClaimModal({ spinId, prize, onClose, onClaimed }) {
  const [addresses, setAddresses] = useState([]);
  const [addrId, setAddrId] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ full_name:"", mobile:"", pincode:"", house:"", area:"", city:"", state:"", landmark:"", address_type:"Home", is_default:false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { (async () => {
    const { data } = await api.get("/addresses");
    setAddresses(data);
    if (data.length) setAddrId((data.find(a=>a.is_default) || data[0]).address_id);
    else setShowNew(true);
  })(); }, []);

  const saveAddr = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/addresses", form);
      setAddresses(p => [...p, data]);
      setAddrId(data.address_id);
      setShowNew(false);
      toast.success("Address saved");
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const claim = async () => {
    if (!addrId) return toast.error("Please add or select an address");
    setSubmitting(true);
    try {
      const { data } = await api.post("/spin/claim", { spin_id: spinId, address_id: addrId });
      toast.success("🎉 Prize claimed! Tracking your delivery.");
      onClaimed(data.order_id);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Claim failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-end sm:place-items-center p-0 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e)=>e.stopPropagation()} className="bg-white text-slate-900 w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-5 sm:p-7 relative max-h-[92vh] overflow-y-auto" data-testid="claim-modal">
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-full hover:bg-slate-100" data-testid="claim-close"><X size={16}/></button>

        <div className="flex items-center gap-3">
          {prize.image && <img src={prize.image} className="w-14 h-14 rounded-xl object-cover" alt=""/>}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF4500] font-bold">Claim your prize</div>
            <div className="font-display text-xl font-bold">{prize.title}</div>
          </div>
        </div>

        <div className="mt-5">
          <h3 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2 flex items-center gap-1"><MapPin size={12}/> Delivery address</h3>
          <div className="space-y-2">
            {addresses.map(a => (
              <label key={a.address_id} className={`block border rounded-xl p-3 cursor-pointer text-sm ${addrId===a.address_id?"border-[#FF4500] bg-[#FF4500]/5":"border-slate-200"}`}>
                <input data-testid={`claim-addr-${a.address_id}`} type="radio" name="claim-addr" className="mr-2" checked={addrId===a.address_id} onChange={()=>setAddrId(a.address_id)} />
                <span className="font-semibold">{a.full_name} · {a.mobile}</span>
                <div className="text-slate-600 mt-1">{a.house}, {a.area}, {a.city}, {a.state} - {a.pincode}</div>
              </label>
            ))}
            {!showNew ? (
              <button data-testid="claim-add-addr" onClick={()=>setShowNew(true)} className="text-sm text-[#FF4500] font-semibold inline-flex items-center gap-1 mt-1">+ Add new address</button>
            ) : (
              <form onSubmit={saveAddr} className="grid grid-cols-2 gap-2 mt-2">
                {["full_name","mobile","pincode","house","area","city","state","landmark"].map(k => (
                  <input key={k} required={k!=="landmark"} value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})}
                    placeholder={k.replace("_"," ")} data-testid={`claim-input-${k}`}
                    className="bg-slate-50 px-3 py-2 rounded-lg text-sm border border-slate-200"/>
                ))}
                <button data-testid="claim-save-addr" className="col-span-2 bg-[#FF4500] text-white py-2 rounded-full text-sm font-semibold">Save Address</button>
              </form>
            )}
          </div>
        </div>

        <button data-testid="claim-submit" onClick={claim} disabled={submitting || !addrId} className="btn-fire w-full mt-5 py-3.5 rounded-full font-bold text-base inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {submitting ? "Claiming…" : <><Gift size={16}/> Confirm Claim & Ship to me</>}
        </button>
        <p className="text-xs text-slate-500 text-center mt-3">Free PAN-India delivery · Ships within 48 hours</p>
      </div>
    </div>
  );
}

function FAQItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-100 rounded-2xl overflow-hidden">
      <button data-testid={testid} onClick={()=>setOpen(v=>!v)} className="w-full p-4 flex items-center justify-between text-left">
        <span className="font-semibold text-sm">{q}</span>
        <ChevronDown size={18} className={`transition ${open?"rotate-180":""}`}/>
      </button>
      {open && <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
}

function truncate(s, n) { return s && s.length > n ? s.slice(0, n-1) + "…" : s; }

const TESTIMONIALS = [
  { name: "Priya R.", city: "Mumbai",   prize: "Smart Fitness Band",        quote: "Spent ₹79, got a brand new fitness band worth ₹1,299. Delivered in 3 days. Loved it!" },
  { name: "Anika S.", city: "Bangalore",prize: "Designer Fashion Tote",     quote: "I was skeptical at first, but the tote arrived exactly as shown. Stylefire really delivers." },
  { name: "Riya M.",  city: "Delhi",    prize: "Wireless Earbuds Pro",      quote: "₹79 for premium earbuds? Insane value. The packaging was beautiful too." },
];

const FAQS = [
  { q: "Is every spin really a winner?",
    a: "Yes — every spin is guaranteed to award one prize from our curated pool. The selection is server-verified, fair, and tamper-proof." },
  { q: "How does delivery work?",
    a: "After your spin reveals your reward, fill in your delivery address. Prizes are dispatched within 48 hours via reliable courier partners. Delivery is free and PAN-India." },
  { q: "Can I get a refund?",
    a: "Spin Festival entries are non-refundable because every spin awards a real product. If your prize doesn't arrive within 7 working days, write to stylefire.in@gmail.com and we will resolve it within 24 hours." },
  { q: "How many times can I spin?",
    a: "You can spin as many times as you like — each ₹79 payment unlocks one fresh spin. Multiple wins are stacked into your account." },
  { q: "What if the wheel lands on something I don't want?",
    a: "All prizes are premium quality and shippable. You're welcome to gift a prize you don't need to a friend — many users do!" },
  { q: "Is the payment secure?",
    a: "Absolutely. All payments are processed by Razorpay using 256-bit SSL encryption. Stylefire never stores your card details." },
];
